// Dev/mock mode (docs/04 §Testing "torture page" dev mode, task ?mock=1).
//
// `?mock=1` swaps the real HTTP transport for this in-memory MockApi backed by
// bundled fixtures in public/mock/, so the ENTIRE player — pairing, manifest
// loop, media cache + atomic swap, playback, watchdog — runs with NO backend.
// Media is still fetched over HTTP (from /mock/*) so Playwright can intercept
// and delay it to prove the atomic swap; only the API JSON is faked here.
//
// The fixture manifests match the docs/03 shape exactly: 2 timeline windows +
// fallback, mixed image/video/url items.

import type { Heartbeat, ManifestResponse, PlayerApiLike, SnapshotUpload } from './api.ts';
import type { PlaybackEvent } from './events.ts';
import type { Manifest, PairResult } from './types.ts';

const MOCK_TOKEN = 'mock-device-token-01';
const MOCK_SCREEN = 'scr_mock000000000001';
const YEAR_MS = 365 * 24 * 60 * 60 * 1000;

// A self-contained page for the type=url item — loads offline (no network).
const URL_ITEM = `data:text/html,${encodeURIComponent(
  `<!doctype html><html><body style="margin:0;height:100vh;display:grid;place-items:center;` +
    `background:linear-gradient(135deg,#3b1d60,#8a2b6b);color:#fff;` +
    `font:700 6vw system-ui">URL&nbsp;ITEM<script>document.title='url'</script></body></html>`,
)}`;

function baseManifest(
  version: string,
  timeline: Manifest['timeline'],
  fallback: Manifest['fallback'],
  overlay?: Manifest['overlay'],
  zones?: Manifest['zones'],
): Manifest {
  return {
    version,
    min_player_version: '0.1.0',
    screen: { id: MOCK_SCREEN, orientation: 'landscape', timezone: 'America/Los_Angeles' },
    poll_interval_s: 2, // fast so ?mock swaps are picked up quickly
    valid_until: Date.now() + YEAR_MS,
    timeline,
    fallback,
    ...(overlay ? { overlay } : {}),
    ...(zones ? { zones } : {}),
  };
}

/** v1: one wide always-active window (teal → bars video → url) + a future window + fallback. */
function makeV1(): Manifest {
  const now = Date.now();
  return baseManifest(
    'sha256-mock-v1',
    [
      {
        from: now - 60_000,
        to: now + YEAR_MS,
        playlist_id: 'pl_mock_primary0001',
        items: [
          { id: 'itm_mock_teal00001', type: 'image', url: '/mock/img_teal.png', duration_ms: 3000, fit: 'cover' },
          { id: 'itm_mock_bars00001', type: 'video', url: '/mock/vid_bars.mp4', duration_ms: 4000, fit: 'contain' },
          { id: 'itm_mock_url000001', type: 'url', url: URL_ITEM, duration_ms: 3000, fit: 'contain' },
          // Generated HTML slide (docs/09 §B): manifest type 'html' — EXACTLY what the
          // server emits for html_template/html_snippet (MEDIA_KIND_TO_MANIFEST_TYPE).
          // Cached self-contained document, played from a blob in the sandboxed iframe.
          // manifest-contract.test.ts asserts every fixture type here is server-emittable.
          { id: 'itm_mock_tmpl00001', type: 'html', url: '/mock/tmpl_promo.html', duration_ms: 3000, fit: 'contain' },
        ],
      },
      {
        // A second window in the far future — proves the timeline holds >1 window.
        from: now + YEAR_MS,
        to: now + YEAR_MS + 3_600_000,
        playlist_id: 'pl_mock_future00001',
        items: [
          { id: 'itm_mock_indigo01', type: 'image', url: '/mock/img_indigo.png', duration_ms: 3000, fit: 'contain' },
        ],
      },
    ],
    {
      items: [
        { id: 'itm_mock_fbindigo1', type: 'image', url: '/mock/img_indigo.png', duration_ms: 3000, fit: 'contain' },
      ],
    },
    // Overlay demo (docs/09 §C2): corner logo (cached like any media) + ticker.
    {
      logo: { url: '/mock/img_indigo.png', position: 'br', size_pct: 12, opacity: 90 },
      ticker: {
        text: 'SEENCAST · brand overlays now live · logo watermark + scrolling ticker',
        speed_px_s: 60,
      },
    },
  );
}

/** v2: introduces img_amber (NEW media) — the atomic-swap test delays its download. */
function makeV2(): Manifest {
  const now = Date.now();
  return baseManifest(
    'sha256-mock-v2',
    [
      {
        from: now - 60_000,
        to: now + YEAR_MS,
        playlist_id: 'pl_mock_primary0002',
        items: [
          { id: 'itm_mock_teal00001', type: 'image', url: '/mock/img_teal.png', duration_ms: 3000, fit: 'cover' },
          { id: 'itm_mock_amber0001', type: 'image', url: '/mock/img_amber.png', duration_ms: 3000, fit: 'cover' },
        ],
      },
    ],
    {
      items: [
        { id: 'itm_mock_teal00001', type: 'image', url: '/mock/img_teal.png', duration_ms: 3000, fit: 'cover' },
      ],
    },
  );
}

/**
 * Zones demo (docs/04 §Zones): a `main` zone (top 82%, driven by the scheduled
 * top-level timeline — teal/bars/url/tmpl) + a `ticker` strip (bottom 18%) looping
 * a single image. Proves multi-engine composition, the cache-plan union, and that
 * the overlay still stacks above every zone.
 */
function makeZones(): Manifest {
  const now = Date.now();
  return baseManifest(
    'sha256-mock-zones',
    [
      {
        from: now - 60_000,
        to: now + YEAR_MS,
        playlist_id: 'pl_mock_zonemain01',
        items: [
          { id: 'itm_mock_teal00001', type: 'image', url: '/mock/img_teal.png', duration_ms: 3000, fit: 'cover' },
          { id: 'itm_mock_bars00001', type: 'video', url: '/mock/vid_bars.mp4', duration_ms: 4000, fit: 'contain' },
        ],
      },
    ],
    {
      items: [
        { id: 'itm_mock_fbindigo1', type: 'image', url: '/mock/img_indigo.png', duration_ms: 3000, fit: 'contain' },
      ],
    },
    {
      logo: { url: '/mock/img_indigo.png', position: 'tr', size_pct: 10, opacity: 90 },
      ticker: { text: 'SEENCAST · zones live · main + ticker', speed_px_s: 60 },
    },
    [
      // main mirrors the top-level timeline (ignored by the player, which reads
      // top-level); a bare all-day window keeps it structurally valid.
      {
        id: 'main',
        rect_pct: { x: 0, y: 0, w: 100, h: 82 },
        timeline: [
          {
            from: now - 60_000,
            to: now + YEAR_MS,
            playlist_id: 'pl_mock_zonemain01',
            items: [
              { id: 'itm_mock_teal00001', type: 'image', url: '/mock/img_teal.png', duration_ms: 3000, fit: 'cover' },
              { id: 'itm_mock_bars00001', type: 'video', url: '/mock/vid_bars.mp4', duration_ms: 4000, fit: 'contain' },
            ],
          },
        ],
      },
      {
        id: 'ticker',
        rect_pct: { x: 0, y: 82, w: 100, h: 18 },
        // Windowed timeline (ManifestWindow[]): a single all-day loop.
        timeline: [
          {
            from: now - 60_000,
            to: now + YEAR_MS,
            playlist_id: 'pl_mock_zoneticker1',
            items: [
              { id: 'itm_mock_zticker01', type: 'image', url: '/mock/img_amber.png', duration_ms: 3000, fit: 'cover' },
            ],
          },
        ],
      },
    ],
  );
}

export class MockApi implements PlayerApiLike {
  private pollCount = 0;
  private current: Manifest;
  private v2 = false;

  constructor() {
    this.current = makeV1();
  }

  /** Flip the mock to serve v2 on the next manifest poll (test hook). */
  serveV2(): void {
    this.current = makeV2();
    this.v2 = true;
  }

  /** Flip the mock to serve the zoned layout on the next manifest poll (test hook). */
  serveZones(): void {
    this.current = makeZones();
  }

  isV2(): boolean {
    return this.v2;
  }

  async pairStart(): Promise<{ code: string }> {
    this.pollCount = 0;
    return { code: 'MOCK42' };
  }

  async pairPoll(): Promise<PairResult | null> {
    // Show the code for one poll cycle, then "operator binds it" → token.
    this.pollCount++;
    if (this.pollCount < 2) return null;
    return { device_token: MOCK_TOKEN, screen_id: MOCK_SCREEN };
  }

  async fetchManifest(_token: string, etag: string | null, _hb: Heartbeat): Promise<ManifestResponse> {
    if (etag === this.current.version) return { manifest: null, etag, command: null };
    return { manifest: this.current, etag: this.current.version, command: null };
  }

  // Analytics/snapshot uploads are no-ops in mock mode (no backend); resolving
  // means the event buffer treats each batch as acknowledged and drains cleanly.
  async postEvents(_token: string, _events: PlaybackEvent[]): Promise<void> {}

  async postSnapshot(_token: string, _snap: SnapshotUpload): Promise<void> {}
}

export { MOCK_TOKEN, MOCK_SCREEN };
